// Dashboard page

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { Layout } from '../components/Layout';
import { LicenseKeyRow } from '../components/LicenseCard';
import { SubscriptionCard } from '../components/SubscriptionCard';
import { SessionWarning } from '../components/SessionWarning';
import { GracePeriodBanner } from '../components/GracePeriodBanner';
import { RotateModal } from '../components/RotateModal';
import { AirgapKeyManager } from '../components/AirgapKeyManager';
import { PlanManager } from '../components/PlanManager';
import { FreeKeyCard } from '../components/FreeKeyCard';
import { ModelCatalogManager } from '../components/ModelCatalogManager';
import { api } from '../services/api';
import { useSession } from '../hooks/useSession';
import type { DashboardData } from '../types';

export function Dashboard() {
  const navigate = useNavigate();
  const { timeRemaining, showWarning } = useSession();
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [rotatingLicenseId, setRotatingLicenseId] = useState<string | null>(null);
  const [isRotating, setIsRotating] = useState(false);

  const email = localStorage.getItem('userEmail') || '';

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const dashboardData = await api.getDashboard();
      setData(dashboardData);
    } catch (err) {
      if (err instanceof Error && err.message === 'Session expired') {
        navigate('/');
      } else {
        setError('Failed to load dashboard data');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await api.logout();
    navigate('/');
  };

  const handleRotate = async () => {
    if (!rotatingLicenseId) return;
    setIsRotating(true);
    try {
      await api.rotateLicense(rotatingLicenseId);
      setRotatingLicenseId(null);
      await loadDashboard();
    } catch (err) {
      alert('Failed to rotate license key. Please try again.');
    } finally {
      setIsRotating(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading your dashboard...</p>
        </div>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <div className="text-center py-16">
          <p className="text-destructive">{error || 'Failed to load data'}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground border border-border hover:bg-secondary hover:text-foreground px-4 py-2 rounded-md transition-colors bg-transparent"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>

        {/* Session Warning */}
        {showWarning && timeRemaining && (
          <SessionWarning timeRemaining={timeRemaining} />
        )}

        <FreeKeyCard freeKey={data.freeKey} onIssued={loadDashboard} />

        {data.subscriptions.map(({ subscription, licenses }) => (
          <SubscriptionCard
            key={subscription.id}
            subscription={subscription}
            email={email}
            onUpdated={loadDashboard}
          >
            {licenses.map(license => (
              <div key={license.id}>
                {license.gracePeriod && (
                  <GracePeriodBanner gracePeriod={license.gracePeriod} />
                )}
                <LicenseKeyRow
                  license={license}
                  onRotate={() => setRotatingLicenseId(license.id)}
                />
              </div>
            ))}
          </SubscriptionCard>
        ))}

        {/* Plans + issued keys (master email only) */}
        {data.isAdmin && <PlanManager />}

        {/* Air-gap keys (master email only) */}
        {data.isAdmin && <AirgapKeyManager />}

        {/* Model catalog broadcast (master email only) */}
        {data.isAdmin && <ModelCatalogManager />}

        {/* Rotate Modal */}
        <RotateModal
          isOpen={rotatingLicenseId !== null}
          onConfirm={handleRotate}
          onCancel={() => setRotatingLicenseId(null)}
          isLoading={isRotating}
        />
      </div>
    </Layout>
  );
}
